"use client";
import { useEffect, useRef } from "react";
import { ourServices } from "src/utils/mock/homePageMock";
import ServiceCard from "src/components/molecules/ServiceCards";
import { animateBadge } from "src/utils/animation/animateBadge";
import SectionHeading from "src/components/molecules/SectionHeading";

export default function ServicesSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    let ctx;

    (async () => {
      try {
        const { gsap } = await import("gsap");
        const { ScrollTrigger } = await import("gsap/ScrollTrigger");

        gsap.registerPlugin(ScrollTrigger);

        let SplitText;
        try {
          ({ SplitText } = await import("gsap/SplitText"));
          gsap.registerPlugin(SplitText);
        } catch {}

        let chroma;
        try {
          chroma = (await import("chroma-js")).default;
        } catch {}

        ctx = gsap.context(() => {
          gsap.fromTo(
            ".services-heading",
            { opacity: 0, y: 40, filter: "blur(6px)" },
            {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              duration: 0.9,
              ease: "power3.out",
              scrollTrigger: {
                trigger: ".services-heading",
                start: "top 88%",
                toggleActions: "play none none none",
              },
            },
          );

          const cards = gsap.utils.toArray(".service-card");
          gsap.set(cards, { opacity: 0, y: 60, scale: 0.95 });

          const icons = gsap.utils.toArray(".service-card__icon");
          gsap.set(icons, { scale: 0.4, opacity: 0, rotate: -15 });

          cards.forEach((card, i) => {
            const fromX = i % 2 === 0 ? -40 : 40;

            gsap.to(card, {
              opacity: 1,
              y: 0,
              x: 0,
              scale: 1,
              duration: 0.75,
              ease: "power3.out",
              delay: (i % 3) * 0.08,
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 85%",
                end: "bottom top",
                toggleActions: "play none none reverse",
              },
            });

            const icon = card.querySelector(".service-card__icon");
            if (icon) {
              gsap.to(icon, {
                scale: 1,
                opacity: 1,
                rotate: 0,
                duration: 0.6,
                ease: "back.out(1.8)",
                delay: (i % 3) * 0.08 + 0.15,
                scrollTrigger: {
                  trigger: sectionRef.current,
                  start: "top 85%",
                  end: "bottom top",
                  toggleActions: "play none none reverse",
                },
              });
            }
          });

          if (SplitText && chroma) {
            const badgeEl = sectionRef.current?.querySelector(".services-badge");
            if (badgeEl) animateBadge(badgeEl);
          }

          const orbs = sectionRef.current?.querySelectorAll(".bg-orb");
          if (orbs?.length) {
            orbs.forEach((orb, i) => {
              gsap.to(orb, {
                y: i % 2 === 0 ? -80 : 80,
                ease: "none",
                scrollTrigger: {
                  trigger: sectionRef.current,
                  start: "top bottom",
                  end: "bottom top",
                  scrub: 1.5,
                },
              });
            });
          }
        }, sectionRef);
      } catch (err) {
        console.warn("[ServicesSection] GSAP animation skipped:", err.message);
      }
    })();

    return () => ctx?.revert();
  }, []);

  return (
    <div>
      <section ref={sectionRef} id="services" className="relative bg-background-secondary overflow-hidden !py-10 sm:!py-8  lg:!py-10 xl:!py-12">
        <div className="absolute inset-0  opacity-60 pointer-events-none" />

        <div className="container mx-auto !px-5 sm:px-6 lg:px-8 relative z-10">
          <div className="services-heading !mb-12 lg:mb-16">
            <SectionHeading
              maxWidth={1100}
              miniTitle="Service"
              miniHighlight="Offerings"
              title="End-to-End Digital Solutions"
              subtitle="From building modern websites to developing high-performance applications, we deliver scalable IT services designed to improve business efficiency and digital growth. Our expertise in web, mobile, cloud, and AI solutions helps businesses create secure, user-friendly, and performance-driven digital experiences."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
            {ourServices.map((service, i) => (
              <ServiceCard key={i} {...service} index={i} className="service-card" />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
